import { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { Check as CheckIcon, Lock as LockIcon } from '@mui/icons-material';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useTranslation } from 'react-i18next';
import { InputStep } from './Steps/InputStep/InputStep';
import { TimelineStep } from './Steps/TimelineStep/TimelineStep';
import { AssetPlanStep } from './Steps/AssetPlanStep/AssetPlanStep';
import { SiteConfigView } from 'views/FleetPlanner/SiteConfig/SiteConfigView';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  type NotificationState,
  showNotification,
} from 'store/notification.slice';
import useAppTheme from 'hooks/useAppTheme';
import { useClientTheme } from 'utils/clientTheme';
import FleetPlanningHandler from 'handlers/fleet-planning.handler';
import { DeliveryStatusStep } from './Steps/DeliveryStatusStep/DeliveryStatusStep';

export const CreateDispatchPlan = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const clientPalette = useClientTheme();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);

  const fleetPlanningHandler = new FleetPlanningHandler();
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(1);

  const selectedPlanData = useAppSelector((state) =>
    state.fleetPlanningReducer.selectedPlan === 'user'
      ? state.fleetPlanningReducer.userPlan
      : state.fleetPlanningReducer.optimizedPlan
  );

  const siteConfigSaved = useAppSelector(
    (state) => state.siteConfigReducer.committedSnapshot !== null
  );
  const planDispatched = useAppSelector(
    (state) => state.fleetPlanningReducer.planDispatched
  );

  const [isLoading, setIsLoading] = useState(true);
  const isLoadingAllAssets = useAppSelector(
    (state) => state.assetReducer.isLoadingAllAssets
  );
  const isPlanLoading = useAppSelector(
    (state) => state.fleetPlanningReducer.isLoading
  );

  const steps = [
    {
      label: t('cdp_step_site'),
      component: (
        <SiteConfigView
          onSaveSuccess={() => {
            setCurrentStep(2);
          }}
        />
      ),
    },
    {
      label: t('cdp_step_inputs'),
      component: <InputStep setCurrentStep={setCurrentStep} shiftWise={true} />,
    },
    { label: t('cdp_step_timeline'), component: <TimelineStep /> },
    {
      label: t('cdp_step_plan'),
      component: <AssetPlanStep setCurrentStep={setCurrentStep} />,
    },
    { label: t('cdp_step_status'), component: <DeliveryStatusStep /> },
  ];

  const hasPlanData = useMemo(
    () =>
      Array.isArray(selectedPlanData.planData) &&
      selectedPlanData.planData.length > 0,
    [selectedPlanData.planData]
  );

  const handleStepClick = (stepNumber: number) => {
    if (isPlanLoading) return;
    if (stepNumber >= 2 && !siteConfigSaved) {
      dispatch(
        showNotification({
          isLoading: false,
          message: t('cdp_site_config_required'),
          show: true,
          type: 'error',
        })
      );
      return;
    }
    if (stepNumber === 5 && !planDispatched) return;
    if (stepNumber <= 2 || hasPlanData) {
      setCurrentStep(stepNumber);
    } else {
      dispatch(
        showNotification({
          isLoading: false,
          message: t('cdp_plan_required_to_view'),
          show: true,
          type: 'error',
        })
      );
    }
  };

  const handleNext = () => {
    handleStepClick(currentStep + 1);
  };

  const handleBack = () => {
    handleStepClick(currentStep - 1);
  };

  useEffect(() => {
    if (accessToken) {
      if (hasPlanData) {
        setIsLoading(false);
      } else {
        void (async () => {
          try {
            setIsLoading(true);
            await fleetPlanningHandler.getTripsStats();
          } catch (error) {
            console.error('Failed to fetch trips stats', error);
          } finally {
            setIsLoading(false);
          }
        })();
      }
    }
  }, [accessToken]);

  return (
    <CustomMuiThemeProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 100px)',
          width: '100%',
          color: 'text.secondary',
          gap: 1,
        }}
      >
        {isLoadingAllAssets || isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              backgroundColor: 'customBackground.section',
            }}
          >
            <SingleFadeLoader isFullScreen={false} message={t('cdp_loading')} />
          </Box>
        ) : (
          <>
            <Box
              component="header"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                p: 1,
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                {steps.map((step, index) => {
                  const stepNumber = index + 1;
                  const isActive = currentStep === stepNumber;
                  const isCompleted = currentStep > stepNumber;
                  const isLocked =
                    (stepNumber >= 2 && !siteConfigSaved) ||
                    (stepNumber === 5 && !planDispatched);

                  return (
                    <Tooltip
                      key={step.label}
                      title={isLocked ? t('cdp_save_site_config_first') : ''}
                      arrow
                    >
                      <Box
                        onClick={() => {
                          handleStepClick(stepNumber);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          opacity: isLocked ? 0.5 : 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            bgcolor: isActive
                              ? 'primary.main'
                              : isCompleted
                              ? '#16a34a'
                              : clientPalette.borderGrey,
                            color: 'white',
                            boxShadow: isActive
                              ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                              : 'none',
                            fontWeight: 'bold',
                            fontSize: 14,
                          }}
                        >
                          {isLocked ? (
                            <LockIcon fontSize="small" />
                          ) : isCompleted ? (
                            <CheckIcon fontSize="small" />
                          ) : (
                            stepNumber
                          )}
                        </Box>
                        <Typography
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            fontSize: 14,
                            color: isActive
                              ? 'primary.main'
                              : isCompleted
                              ? '#16a34a'
                              : 'text.primary',
                          }}
                        >
                          {step.label}
                        </Typography>
                        {stepNumber < steps.length && (
                          <Box
                            sx={{
                              width: 40,
                              height: 2,
                              bgcolor: isCompleted
                                ? '#16a34a'
                                : clientPalette.borderGrey,
                              mx: 1.5,
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>

              {/* Back/Next Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {currentStep > 1 && (
                  <CustomLoadingButton
                    variant="outlined"
                    color="primary"
                    disabled={isPlanLoading}
                    onClick={handleBack}
                  >
                    {t('cdp_button_back')}
                  </CustomLoadingButton>
                )}
                {currentStep < steps.length && (
                  <Tooltip
                    title={
                      currentStep === 1 && !siteConfigSaved
                        ? t('cdp_save_site_config_first')
                        : currentStep === 2 && !hasPlanData
                        ? t('cdp_plan_required_to_view')
                        : ''
                    }
                    arrow
                  >
                    <span>
                      <CustomLoadingButton
                        variant="contained"
                        color="primary"
                        disabled={
                          isPlanLoading ||
                          (currentStep === 1 && !siteConfigSaved) ||
                          (currentStep === 2 && !hasPlanData) ||
                          (currentStep === 4 && !planDispatched)
                        }
                        onClick={handleNext}
                      >
                        {t('cdp_button_next')}
                      </CustomLoadingButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Divider
              flexItem
              sx={{
                borderColor: clientPalette.borderGrey,
                borderRightWidth: 2,
              }}
            />

            {/* --- Main Content Area --- */}
            <Box component="main" sx={{ flex: 1, px: 1, overflowY: 'auto' }}>
              {steps[currentStep - 1].component}
            </Box>
          </>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};
